import { Outlet, Navigate, useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { Suspense, useEffect, useState } from 'react'
import { HybridLayout } from '@/components/ui/HybridLayout'
import AppSidebar from './Sidebar'
import AppNavbar from './AppNavbar'
import { usePageLoading } from '@/contexts/LoadingContext'
import { useSelectionStore } from '@/state/selection.store'
import { getWorkspaceProjectFromParams } from '@/lib/utils'
import { useWorkspaces } from '@/features/workspace/hooks/useWorkspaces'
import { shouldHideSidebar, shouldShowFreeGenerationBanner } from '@/app/router/routeConfig'
import { ACCOUNTS_URL } from '@/lib/constants'
import { safeLocalStorage } from '@/lib/safe-storage'
import PricingIframeModal from '@/features/pricing/components/PricingIframeModal'
import IntercomManager from '@/components/layout/IntercomManager'
import FloatingWorkspaceSwitcher from '@/components/layout/FloatingWorkspaceSwitcher'
import NoWorkspacePage from '@/features/workspace/pages/NoWorkspacePage'
import CriticalErrorPage from '@/components/error/CriticalErrorPage'
import usePageTitle from '@/hooks/usePageTitle'
import { AdminBanner } from '@/components/layout/AdminBanner'
import { useAdminModeStore } from '@/hooks/useAdminMode'
import { useUserProfile } from '@/features/profile/hooks/useUserProfile'
import { HomeFreeGenerationBanner } from '@/features/home/components/HomeFreeGenerationBanner'
import { shouldRenderFreeGenerationBanner } from '@/features/home/lib/freeGenerationBanner'
import { useUIStore } from '@/state/ui.store'

const FREE_GENERATION_BANNER_FEATURE = 'free_generation_banner'

export default function AppShell() {
  usePageTitle()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { workspace, project } = getWorkspaceProjectFromParams(searchParams)
  const { setActiveWorkspace, setActiveProject, activeWorkspaceId } = useSelectionStore()
  const setShowPricingModal = useUIStore(state => state.setShowPricingModal)
  const { workspaces, loading: workspacesLoading, error: workspacesError } = useWorkspaces()
  const { profile, isLoading: profileLoading } = useUserProfile()
  const [hasRedirected, setHasRedirected] = useState(false)

  // Handle ?adminUserId= deep-link from external admin panel
  useEffect(() => {
    const adminUserId = searchParams.get('adminUserId')
    if (!adminUserId) return

    const store = useAdminModeStore.getState()
    // Only enter if we're not already viewing this user
    if (store.isAdminMode && store.targetUserId === adminUserId) return

    // Check if current user is admin
    try {
      const raw = safeLocalStorage.getItem('user_data')
      if (!raw) return
      const user = JSON.parse(raw)
      if (!user.isAdmin) return
    } catch { return }

    // Auto-enter admin mode and fetch that user's workspaces
    store.enterAdminMode(adminUserId, adminUserId)

    // Also switch to one of their workspaces if workspace param is present
    const wsParam = searchParams.get('workspace')
    if (wsParam) {
      setActiveWorkspace(wsParam)
    }
  }, [searchParams, setActiveWorkspace])

  // Register workspace-level loading with the central loader
  // Stop waiting if there's an error — otherwise the loader stays on top of the error page
  const isWaitingForWorkspace = !workspace && !workspacesError && (workspacesLoading || !hasRedirected)
  usePageLoading('appshell-workspace', isWaitingForWorkspace)

  const getPersistedWorkspaceId = () => {
    try {
      const persisted = safeLocalStorage.getItem('codedesign-selection')
      if (persisted) {
        const parsed = JSON.parse(persisted)
        return parsed?.state?.activeWorkspaceId || null
      }
    } catch (e) {
      console.error('Error parsing persisted workspace ID:', e)
    }
    return null
  }

  useEffect(() => {
    if (workspace) setActiveWorkspace(workspace)
    if (project) {
      setActiveProject(project)
    } else {
      // Clear project state when navigating to a non-project page (e.g. back to workspace)
      setActiveProject(null)
    }
  }, [workspace, project, setActiveWorkspace, setActiveProject])

  useEffect(() => {
    // Don't run redirect logic if there's an error
    if (workspacesError) return

    // Check if current user is a platform admin
    let isAdminUser = false
    try {
      const raw = safeLocalStorage.getItem('user_data')
      isAdminUser = raw && JSON.parse(raw).isAdmin === true
    } catch { /* ignore */ }

    // Admins can access any workspace — if one is already in the URL, nothing to do
    if (isAdminUser && workspace) return

    if (!workspace && !workspacesLoading && !hasRedirected) {
      let targetId = getPersistedWorkspaceId() || activeWorkspaceId

      // For non-admins, validate the workspace belongs to them
      if (targetId && workspaces?.length > 0 && !isAdminUser) {
        const exists = workspaces.some(ws => {
          const id = ws.id || ws._id
          return id && String(id) === String(targetId)
        })
        if (!exists) targetId = null
      }

      // Check if we have a saved post-auth redirect path
      // (handles case where accounts.codedesign.ai strips the path and redirects to /)
      const savedRedirect = safeLocalStorage.getItem('postAuthRedirect')
      let targetPathname = location.pathname
      if (savedRedirect) {
        safeLocalStorage.removeItem('postAuthRedirect')
        // Only use saved path if we're currently at root (meaning accounts stripped it)
        if (location.pathname === '/' || location.pathname === '') {
          const savedPath = savedRedirect.split('?')[0]
          targetPathname = savedPath || '/'
        }
      }

      if (targetId) {
        setHasRedirected(true)
        setActiveWorkspace(targetId)

        const newParams = new URLSearchParams(searchParams)
        newParams.set('workspace', targetId)
        navigate(`${targetPathname}?${newParams.toString()}`, { replace: true })
        return
      }

      if (workspaces?.length > 0) {
        const defaultWorkspace = workspaces.find(ws => ws.isDefault) || workspaces[0]
        const fallbackId = defaultWorkspace?.id || defaultWorkspace?._id
        if (fallbackId) {
          setHasRedirected(true)
          setActiveWorkspace(fallbackId)

          const newParams = new URLSearchParams(searchParams)
          newParams.set('workspace', fallbackId)
          navigate(`${targetPathname}?${newParams.toString()}`, { replace: true })
        }
      } else {
        // No workspaces available (empty or error) - mark as redirected to stop the loop
        setHasRedirected(true)
      }
    }
  }, [workspace, workspaces, workspacesLoading, workspacesError, hasRedirected, activeWorkspaceId, setActiveWorkspace, navigate, location, searchParams])

  // If workspaces API failed, show the critical error page
  if (workspacesError && !workspacesLoading) {
    return <CriticalErrorPage error={workspacesError} />
  }

  // If no workspaces (empty array), show the no workspace page - regardless of URL
  if (!workspacesLoading && (!workspaces || workspaces.length === 0)) {
    return <NoWorkspacePage />
  }

  // For non-admin users: if workspace is in the URL but doesn't belong to them,
  // redirect to their default workspace BEFORE rendering child routes.
  // This prevents child components from firing failed API calls for inaccessible workspaces.
  const isAdmin = (() => {
    try {
      const raw = safeLocalStorage.getItem('user_data')
      return raw && JSON.parse(raw).isAdmin === true
    } catch { return false }
  })()

  if (workspace && !workspacesLoading && workspaces?.length > 0 && !isAdmin) {
    const workspaceExists = workspaces.some(ws => {
      const id = ws.id || ws._id
      return id && String(id) === String(workspace)
    })
    if (!workspaceExists) {
      const defaultWs = workspaces.find(ws => ws.isDefault) || workspaces[0]
      const fallbackId = defaultWs?.id || defaultWs?._id
      if (fallbackId) {
        const newParams = new URLSearchParams(searchParams)
        newParams.set('workspace', fallbackId)
        return <Navigate to={`${location.pathname}?${newParams.toString()}`} replace />
      }
    }
  }

  // While waiting for workspace (no workspace in URL), render nothing - CentralLoader handles UI
  if (!workspace) {
    if (!safeLocalStorage.getItem('auth_token')) {
      // Save the intended destination so we can restore it after login
      // (in case accounts.codedesign.ai strips the path from the redirect)
      const intendedPath = location.pathname + location.search
      safeLocalStorage.setItem('postAuthRedirect', intendedPath)

      // For /ai route: save prompt params to localStorage before auth redirect
      // (AIOnboardIndex won't mount in time to do this itself)
      if (location.pathname === '/ai') {
        const urlParams = new URLSearchParams(window.location.search)
        const prompt = urlParams.get('prompt') || ''
        if (prompt) {
          const aiOnboardParams = {
            type: urlParams.get('type') || '',
            objective: urlParams.get('objective') || '',
            step: urlParams.get('step') || '',
            language: urlParams.get('language') || '',
            prompt: prompt,
            templateId: urlParams.get('templateId') || '',
            setting: urlParams.get('setting') || '',
          }
          safeLocalStorage.setItem('aiOnboardParams', JSON.stringify(aiOnboardParams))
        }
        // Use clean redirect URL (prompt is in localStorage, not needed in URL)
        const redirectUrl = window.location.origin + '/ai'
        window.location.href = `${ACCOUNTS_URL}/register?redirect=${encodeURIComponent(redirectUrl)}`
        return null
      }
      const redirectUrl = window.location.origin + window.location.pathname + window.location.search
      window.location.href = `${ACCOUNTS_URL}/login?redirect=${encodeURIComponent(redirectUrl)}`
      return null
    }
    // CentralLoader is already showing, just wait
    if (workspacesLoading || !hasRedirected) {
      return null
    }
    // Redirect to the selected workspace
    if (activeWorkspaceId) {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('workspace', activeWorkspaceId)
      return <Navigate to={`${location.pathname}?${newParams.toString()}`} replace />
    }
    return null
  }

  // EMBED MODE (?embed=1): the page renders bare — no navbar, sidebar or
  // banners. Used by the Website Studio to open in-app feature pages
  // (analytics, forms, blog, …) inside popups instead of navigating away.
  if (searchParams.get('embed') === '1') {
    return (
      <>
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
        <PricingIframeModal />
      </>
    )
  }

  const hideSidebar = shouldHideSidebar(location.pathname)
  const currentWorkspace =
    workspaces?.find(ws => String(ws.id || ws._id) === String(workspace || activeWorkspaceId)) ||
    workspaces?.[0]
  const shouldRenderTopBanner =
    shouldShowFreeGenerationBanner(location.pathname) &&
    shouldRenderFreeGenerationBanner({
      workspace: currentWorkspace,
      profile,
      loading: workspacesLoading || profileLoading,
    })
  const topBanner = shouldRenderTopBanner ? (
    <HomeFreeGenerationBanner
      workspace={currentWorkspace}
      profile={profile}
      loading={false}
      onUpgradeClick={() => setShowPricingModal(true, FREE_GENERATION_BANNER_FEATURE)}
    />
  ) : null

  // Pages that intentionally hide the sidebar but still need workspace
  // switching (templates browser + pricing). On `/ai` and `/` the sidebar
  // is also hidden but those flows have their own chrome — don't add the
  // floating pill there.
  const showFloatingWorkspaceSwitcher =
    hideSidebar &&
    (location.pathname === '/pricing' ||
      location.pathname === '/templates' ||
      location.pathname.startsWith('/templates/'))

  return (
    <>
      <AdminBanner />
      <HybridLayout
        navbar={<AppNavbar />}
        sidebar={<AppSidebar />}
        hideSidebar={hideSidebar}
        topBanner={topBanner}
      >
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
      </HybridLayout>
      {showFloatingWorkspaceSwitcher && <FloatingWorkspaceSwitcher />}
      <PricingIframeModal />
      <IntercomManager />
    </>
  )
}
